import * as THREE from "three";
import { humanSprite, type HumanPose } from "./CharacterSprites";
import { makeContactShadow } from "./ContactShadow";
import { billboardMaterial, faceCameraYaw, facingFlipSign, SPRITE_FOOT_Y } from "./WorldArt";
import { mat } from "./WorldModels";

export type PicnicKind = "family" | "date" | "friends";

export interface ParkPicnicOpts {
  /** World yaw people should face (e.g. toward the amph stage). */
  faceYaw?: number;
  /** Skip basket / snacks for denser amph lawn clusters. */
  compact?: boolean;
}

/**
 * Ambient picnic: blanket, basket, and people lounging / snacking on the lawn.
 */
export class ParkPicnic {
  readonly group = new THREE.Group();
  private readonly people: {
    mesh: THREE.Mesh;
    shadow: THREE.Mesh;
    x: number;
    z: number;
    y: number;
    faceYaw: number;
    poseA: ReturnType<typeof humanSprite>;
    poseB: ReturnType<typeof humanSprite>;
    phase: number;
  }[] = [];

  constructor(
    midX: number,
    midZ: number,
    groundY: (x: number, z: number) => number,
    kind: PicnicKind = "family",
    variant = 0,
    opts: ParkPicnicOpts = {},
  ) {
    const gy = groundY(midX, midZ);
    const faceYaw = opts.faceYaw;
    const compact = opts.compact === true;
    // Orient the blanket toward the stage when watching a show; otherwise a soft random twist.
    const yaw = faceYaw ?? (variant % 8) * (Math.PI / 8);
    const blanketColors = [0xf48fb1, 0x81d4fa, 0xffe082, 0xce93d8, 0xa5d6a7, 0xffcc80];
    const blanketColor = blanketColors[variant % blanketColors.length]!;

    const blanketW = compact
      ? kind === "date" ? 2.0 : 2.55
      : kind === "date" ? 2.4 : 3.2;
    const blanketD = compact
      ? kind === "date" ? 1.7 : 2.15
      : kind === "date" ? 2.0 : 2.6;

    // Soft picnic blanket on the grass.
    const blanket = new THREE.Mesh(
      new THREE.PlaneGeometry(blanketW, blanketD),
      new THREE.MeshLambertMaterial({ color: blanketColor }),
    );
    blanket.rotation.x = -Math.PI / 2;
    blanket.rotation.z = faceYaw != null ? faceYaw + Math.PI / 2 : yaw * 0.35;
    blanket.position.set(midX, gy + 0.055, midZ);
    blanket.receiveShadow = false;
    const blanketMat = blanket.material as THREE.MeshLambertMaterial;
    blanketMat.polygonOffset = true;
    blanketMat.polygonOffsetFactor = -2;
    blanketMat.polygonOffsetUnits = -2;
    blanketMat.depthWrite = false;
    this.group.add(blanket);

    if (!compact) {
      // Wicker basket
      const basket = new THREE.Group();
      const body = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.32, 0.34, 8), mat(0xb08968));
      body.position.y = 0.2;
      const lid = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.06, 8), mat(0x8d6e4c));
      lid.position.y = 0.4;
      const handle = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.03, 6, 12, Math.PI), mat(0x6d4c41));
      handle.rotation.x = Math.PI / 2;
      handle.position.y = 0.48;
      basket.add(body, lid, handle);
      const bx = midX + Math.cos(yaw) * 0.85;
      const bz = midZ + Math.sin(yaw) * 0.85;
      basket.position.set(bx, groundY(bx, bz), bz);

      // Little food / plate props on the blanket.
      const plate = new THREE.Mesh(
        new THREE.CylinderGeometry(0.22, 0.22, 0.04, 12),
        mat(0xfff8e1),
      );
      plate.position.set(midX - Math.cos(yaw) * 0.35, gy + 0.08, midZ - Math.sin(yaw) * 0.35);
      const fruit = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 6), mat(0xef5350));
      fruit.position.set(plate.position.x + 0.05, gy + 0.14, plate.position.z);
      const cheese = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.06, 0.12), mat(0xffe082));
      cheese.position.set(midX + Math.cos(yaw + 1.2) * 0.45, gy + 0.09, midZ + Math.sin(yaw + 1.2) * 0.45);

      this.group.add(basket, plate, fruit, cheese);
    } else {
      // Tiny snack cue so amph blankets still read as picnics without clutter.
      const crumb = new THREE.Mesh(new THREE.SphereGeometry(0.07, 6, 5), mat(0xffe082));
      crumb.position.set(midX + 0.35, gy + 0.1, midZ - 0.2);
      this.group.add(crumb);
    }

    const seats = picnicSeats(kind, midX, midZ, yaw, compact);
    seats.forEach((seat, i) => {
      const look = variant * 7 + i * 11;
      const poseIdle: HumanPose = seat.pose;
      // Only cycle sit ↔ eat (both canvas). Never swap to standing/chat/toon
      // poses — that morphs one person into a different body every few seconds.
      const poseAlt: HumanPose =
        poseIdle === "eat" ? "sit" : poseIdle === "sit" ? "eat" : poseIdle;
      const sheetA = humanSprite(poseIdle, look);
      const sheetB =
        poseAlt === poseIdle ? sheetA : humanSprite(poseAlt, look);
      const y = groundY(seat.x, seat.z);
      const seated = poseIdle === "eat" || poseIdle === "sit";
      const footY = sheetA.footY ?? SPRITE_FOOT_Y;
      const seatAnchor = sheetA.seatAnchor ?? (poseIdle === "sit" ? 0.46 : 0.36);
      // Blanket sit: hips near the cloth. Clamp so bench-tuned seatAnchors
      // (especially Kenney duck frames) can't bury half the sprite in the turf.
      const blanketY = y + 0.1;
      const rawBottom = seated ? blanketY - seatAnchor * sheetA.height : y;
      const bottom = seated ? Math.max(y + 0.05, rawBottom) : rawBottom;
      const meshY = bottom + sheetA.height * footY;
      const shadow = makeContactShadow({
        radius: Math.min(0.52, sheetA.width * 0.38),
        squash: 1,
        opacity: compact ? 0.55 : 0.78,
      });
      shadow.position.set(seat.x, y + 0.025, seat.z);
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(sheetA.width, sheetA.height),
        billboardMaterial(sheetA.texture),
      );
      mesh.position.set(seat.x, meshY, seat.z);
      mesh.renderOrder = 3;
      this.group.add(shadow, mesh);
      this.people.push({
        mesh,
        shadow,
        x: seat.x,
        z: seat.z,
        y: meshY,
        // Watch the show when facing is provided; otherwise look into the blanket.
        faceYaw: faceYaw ?? seat.faceYaw,
        poseA: sheetA,
        poseB: sheetB,
        phase: i * 1.7 + variant * 0.4,
      });
    });
  }

  update(timeMs: number, camera: THREE.Camera): void {
    for (const person of this.people) {
      // Occasional pose swap so picnics feel alive (eat ↔ chat).
      const cycle = ((timeMs * 0.001 + person.phase) % 8) / 8;
      const sheet = cycle < 0.55 ? person.poseA : person.poseB;
      const mat = person.mesh.material;
      if (mat instanceof THREE.MeshBasicMaterial && mat.map !== sheet.texture) {
        mat.map = sheet.texture;
        mat.needsUpdate = true;
      }
      const bob = 1 + Math.sin(timeMs * 0.004 + person.phase) * 0.01;
      person.mesh.position.y = person.y * bob + (1 - bob) * (person.y - 0.02);
      faceCameraYaw(person.mesh, camera);
      person.mesh.scale.set(facingFlipSign(person.x, person.z, person.faceYaw, camera), 1, 1);
    }
  }

  destroy(): void {
    this.group.removeFromParent();
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        if (!obj.geometry.userData.shared) {
          obj.geometry.dispose();
        }
        const m = obj.material;
        if (Array.isArray(m)) {
          m.forEach((mm) => {
            if (!mm.userData.shared) mm.dispose();
          });
        } else if (m && !m.userData.shared) {
          m.dispose();
        }
      }
    });
  }
}

function picnicSeats(
  kind: PicnicKind,
  midX: number,
  midZ: number,
  yaw: number,
  compact = false,
): { x: number; z: number; faceYaw: number; pose: HumanPose }[] {
  const toward = (ox: number, oz: number) => Math.atan2(midX - ox, midZ - oz);
  const s = compact ? 0.82 : 1;
  if (kind === "date") {
    const a = offset(midX, midZ, yaw, -0.7 * s, 0.15 * s);
    const b = offset(midX, midZ, yaw, 0.75 * s, -0.1 * s);
    return [
      { x: a.x, z: a.z, faceYaw: toward(a.x, a.z) + 0.35, pose: "eat" },
      { x: b.x, z: b.z, faceYaw: toward(b.x, b.z) - 0.35, pose: "sit" },
    ];
  }
  if (kind === "friends") {
    const seats = [
      offset(midX, midZ, yaw, -0.95 * s, 0.35 * s),
      offset(midX, midZ, yaw, 0.15 * s, -0.85 * s),
      offset(midX, midZ, yaw, 0.95 * s, 0.45 * s),
    ];
    const poses: HumanPose[] = ["eat", "sit", "eat"];
    return seats.map((seat, i) => ({
      x: seat.x,
      z: seat.z,
      faceYaw: toward(seat.x, seat.z),
      pose: poses[i]!,
    }));
  }
  // Family: two adults + kid + grandparent-ish
  const seats = [
    offset(midX, midZ, yaw, -1.05 * s, 0.4 * s),
    offset(midX, midZ, yaw, 1.0 * s, 0.35 * s),
    offset(midX, midZ, yaw, -0.15 * s, -0.95 * s),
    offset(midX, midZ, yaw, 0.55 * s, -0.55 * s),
  ];
  const poses: HumanPose[] = ["eat", "sit", "sit", "eat"];
  return seats.map((seat, i) => ({
    x: seat.x,
    z: seat.z,
    faceYaw: toward(seat.x, seat.z),
    pose: poses[i]!,
  }));
}

function offset(
  mx: number,
  mz: number,
  yaw: number,
  along: number,
  side: number,
): { x: number; z: number } {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  return {
    x: mx + c * along - s * side,
    z: mz + s * along + c * side,
  };
}

/** Parse picnic kind from scenery id (`picnic-date-1`, `amp-picnic-family-3`, …). */
export function picnicKindFromId(id: string): PicnicKind {
  if (id.includes("date")) return "date";
  if (id.includes("friend")) return "friends";
  return "family";
}
